import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiClient } from '../lib/auth';

export interface ShareLink {
  token: string;
  url: string;
  expiresAt: string;
  createdBy: string;
  mediaIds: string[];
  revoked: boolean;
}

export interface ShareState {
  links: ShareLink[];
  /** IDs of media items currently selected for sharing */
  selectedMediaIds: string[];
  /** The most recently generated link (shown in the share dialog) */
  generatedLink: ShareLink | null;
  loading: boolean;
  error: string | null;
}

const initialState: ShareState = {
  links: [],
  selectedMediaIds: [],
  generatedLink: null,
  loading: false,
  error: null,
};

// ---------------------------------------------------------------------------
// Async thunks
// ---------------------------------------------------------------------------

/**
 * Fetches all shareable links for the current user.
 * Admins see all links; regular internal users see only their own.
 */
export const fetchShareLinks = createAsyncThunk('share/fetchLinks', async () => {
  const response = await apiClient.get<{ links: ShareLink[] }>('/share');
  return response.data.links;
});

/**
 * Generates a new shareable link for the selected media files.
 */
export const generateShareLink = createAsyncThunk(
  'share/generateLink',
  async (params: { mediaIds: string[]; expiryHours: number }) => {
    const response = await apiClient.post<{
      token: string;
      url: string;
      expiresAt: string;
    }>('/share', {
      mediaIds: params.mediaIds,
      expiryHours: params.expiryHours,
    });

    // Return a ShareLink object matching our state shape
    return {
      token: response.data.token,
      url: response.data.url,
      expiresAt: response.data.expiresAt,
      createdBy: '', // Will be populated by the backend in the list
      mediaIds: params.mediaIds,
      revoked: false,
    } as ShareLink;
  },
);

/**
 * Revokes a shareable link by token.
 */
export const revokeShareLink = createAsyncThunk('share/revokeLink', async (token: string) => {
  await apiClient.delete(`/share/${token}`);
  return token;
});

const shareSlice = createSlice({
  name: 'share',
  initialState,
  reducers: {
    setShareLinks(state, action: PayloadAction<ShareLink[]>) {
      state.links = action.payload;
      state.loading = false;
      state.error = null;
    },
    addShareLink(state, action: PayloadAction<ShareLink>) {
      state.links.unshift(action.payload);
      state.generatedLink = action.payload;
    },
    setGeneratedLink(state, action: PayloadAction<ShareLink | null>) {
      state.generatedLink = action.payload;
    },
    toggleMediaSelection(state, action: PayloadAction<string>) {
      const idx = state.selectedMediaIds.indexOf(action.payload);
      if (idx === -1) {
        state.selectedMediaIds.push(action.payload);
      } else {
        state.selectedMediaIds.splice(idx, 1);
      }
    },
    clearMediaSelection(state) {
      state.selectedMediaIds = [];
    },
    setShareLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setShareError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    // fetchShareLinks
    builder
      .addCase(fetchShareLinks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchShareLinks.fulfilled, (state, action) => {
        state.links = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchShareLinks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch shareable links';
      });

    // generateShareLink
    builder
      .addCase(generateShareLink.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateShareLink.fulfilled, (state, action) => {
        state.links.unshift(action.payload);
        state.generatedLink = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(generateShareLink.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to generate shareable link';
      });

    // revokeShareLink
    builder
      .addCase(revokeShareLink.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(revokeShareLink.fulfilled, (state, action) => {
        const link = state.links.find((l) => l.token === action.payload);
        if (link) {
          link.revoked = true;
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(revokeShareLink.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to revoke shareable link';
      });
  },
});

export const {
  setShareLinks,
  addShareLink,
  setGeneratedLink,
  toggleMediaSelection,
  clearMediaSelection,
  setShareLoading,
  setShareError,
} = shareSlice.actions;

// Selectors
export const selectShareLinks = (state: { share: ShareState }) => state.share.links;
export const selectSelectedMediaIds = (state: { share: ShareState }) => state.share.selectedMediaIds;
export const selectGeneratedLink = (state: { share: ShareState }) => state.share.generatedLink;
export const selectShareLoading = (state: { share: ShareState }) => state.share.loading;

export default shareSlice.reducer;
